import React, { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

import ActiveComponentContext from "../../../contexts/ActiveComponentContext";
import { convertCoverPageDate, toDollar } from "../../../helpers";
import useOutputReportStore from "../../../store/OutputReport.store";
import { getOutputType } from "../../../utils/helpers/dataTable";
import Skeleton from "../../Skeleton";

import "./coverPage.scss";

const getReportTitle = (reportType) =>
  reportType === "transition"
    ? "Remi transition proposal portfolio"
    : "Remi cash proposal portfolio";

const CoverPage = () => {
  const data = useOutputReportStore((state) => state.data);
  const { active: activeId, setActive: setActiveComponent } = useContext(
    ActiveComponentContext
  );
  const [coverData, setCoverData] = useState({
    title: "",
    reportId: "",
    client: "",
    advisor: "",
    advisorEmail: "",
    date: "",
    strategy: "",
    initialFunding: "",
  });
  const { ref, entry } = useInView({
    /* Optional options */
    threshold: 0.25,
    trackVisibility: true,
    delay: 100,
  });

  useEffect(() => {
    if (entry?.isVisible && entry?.target?.id !== activeId) {
      setActiveComponent(entry.target.id);
    }
  }, [entry, setActiveComponent, activeId]);

  useEffect(() => {
    if (data) {
      const cover = data["Cover"];

      const { reportType } = getOutputType(data);
      const coverInfo = {
        title: getReportTitle(reportType),
        reportId: cover["ReportId"],
        client: cover["Client"],
        advisor: cover["Advisor"],
        advisorEmail: cover["Advisor Email"],
        date: convertCoverPageDate(cover["Report Date"]),
        strategy: cover["Strategy"],
        initialFunding: toDollar(cover["Initial Funding Amount"]),
      };

      setCoverData(coverInfo);
    }
  }, [data]);

  return (
    <header ref={ref} id="cover" className="cover-page">
      <div className="cover-page__heading">
        <div className="cover-page__wrapper">
          <div className="cover-page__logo" />
          <div className="cover-page__title-container">
            <Skeleton
              theme="primary"
              size="large"
              gap={20}
              count={3}
              settings={{ width: 600 }}
              loaded={!!coverData.title}
            >
              <h1 className="cover-page__title">{coverData.title}</h1>
            </Skeleton>
          </div>

          <div className="cover-page__strategy">
            <Skeleton
              theme="primary"
              size="medium"
              settings={{ width: 600 }}
              loaded={!!coverData.reportId}
            >
              {coverData.strategy}
            </Skeleton>
          </div>

          <div className="cover-page__detail">
            <Skeleton
              theme="primary"
              size="small"
              settings={{ width: 300 }}
              loaded={!!coverData.reportId}
            >
              <span className="cover-page__label">Prepared for</span>
              <span className="cover-page__value">{coverData.client}</span>
            </Skeleton>
          </div>
          <div className="cover-page__detail">
            <Skeleton
              theme="primary"
              size="small"
              settings={{ width: 300 }}
              loaded={!!coverData.reportId}
            >
              <span className="cover-page__label">Prepared by</span>
              <span className="cover-page__value">{coverData.advisor}</span>
            </Skeleton>
          </div>
          {/* TODO: Commented until decided to permanently remove */}
          {/* <div className="cover-page__detail">
            <Skeleton
              theme="primary"
              size="small"
              settings={{ width: 300 }}
              loaded={!!coverData.reportId}
            >
              Contact
              <span className="cover-page__value">{coverData.advisor}</span>
              <span className="cover-page__sub">{coverData.advisorEmail}</span>
            </Skeleton>
          </div> */}
          <div className="cover-page__detail">
            <Skeleton
              theme="primary"
              size="small"
              settings={{ width: 300 }}
              loaded={!!coverData.reportId}
            >
              <span className="cover-page__label">Report identifier</span>
              <span className="cover-page__value">{coverData.reportId}</span>
            </Skeleton>
          </div>
          <div className="cover-page__detail">
            <Skeleton
              theme="primary"
              size="small"
              settings={{ width: 300 }}
              loaded={!!coverData.reportId}
            >
              <span className="cover-page__label">Date</span>
              <span className="cover-page__value">{coverData.date}</span>
            </Skeleton>
          </div>
          <div className="cover-page__detail">
            <Skeleton
              theme="primary"
              size="small"
              settings={{ width: 300 }}
              loaded={!!coverData.reportId}
            >
              <span className="cover-page__label">Initial funding amount</span>
              <span className="cover-page__value">
                {coverData.initialFunding}
              </span>
            </Skeleton>
          </div>
        </div>
      </div>
      <div className="cover-page__info">
        <div className="cover-page__wrapper">
          <div className="cover-page__disclosure">
            <p>
              All data in this report is as of the report date listed above.
            </p>
            <p>
              This information is prepared exclusively at the specific request
              of, and for the sole use of the intended recipient and may contain
              privileged information.
            </p>
            <p>
              Any unauthorized review, use, disclosure or distribution is
              prohibited. IMPORTANT: This material is provided exclusively to
              Allspring's financial intermediaries only for their internal use
              or for use with their clients or prospective clients to which the
              hypothetical performance contained within is relevant to the
              client's investment objectives and likely financial situation, and
              who have the resources and requisite experience or expertise to
              assess the hypothetical performance and understand its risks and
              limitations. All other uses of this material are unauthorized.
            </p>
          </div>
          <div className="cover-page__footer-text">
            <p>
              For Investment Professional Use Only In One-On-One Presentation
              With Client
            </p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default CoverPage;
